import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'
import { useTheme } from '@mui/material'
import { ArrowCircleLeft, ArrowCircleRight } from 'iconsax-react'

import Image from './Image'
import Video from './Video'
import Other from './Other'

import { selectChatBox, updateValue } from '../../../../../slices/chatBoxSlice'
import useStyles from './styles'

export const ID = 'attachments-bar'

const Attachments = ({ data, handleRemove }) => {
  const dispatch = useAppDispatch()
  const { values } = useAppSelector(selectChatBox)
  const theme = useTheme()

  const files = React.useMemo(
    () => data ?? values.file ?? [],
    [data, values.file]
  )
  const [scrollAmount, setScrollAmount] = React.useState(100)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)

  const classes = useStyles()

  const removeFile = React.useCallback(
    i => {
      if (handleRemove) {
        handleRemove(i)
      } else {
        dispatch(
          updateValue('file', old => {
            const files = [...old]
            files.splice(i, 1)
            return files
          })
        )
      }
    },
    [dispatch, handleRemove]
  )

  const checkScrolling = elem => {
    const { scrollLeft, scrollWidth, clientWidth } = elem
    const scrollLeftMax = scrollWidth - clientWidth

    const newScrollAmount = scrollLeftMax / 1.5
    if (newScrollAmount !== scrollAmount) {
      setScrollAmount(newScrollAmount)
    }

    const newLeft = scrollLeft > 0
    if (newLeft !== canScrollLeft) {
      setCanScrollLeft(newLeft)
    }

    const newRight = scrollLeft < scrollLeftMax
    if (newRight !== canScrollRight) {
      setCanScrollRight(newRight)
    }
  }

  // eslint-disable-next-line
  React.useEffect(() => {
    const elem = document.querySelector(`#${ID}`)
    if (elem) {
      checkScrolling(elem)
    }
  })

  const moveLeft = () => {
    const elem = document.querySelector(`#${ID}`)
    if (elem) {
      elem.scrollLeft -= scrollAmount
      checkScrolling(elem)
    }
  }

  const moveRight = () => {
    const elem = document.querySelector(`#${ID}`)
    if (elem) {
      elem.scrollLeft += scrollAmount
      checkScrolling(elem)
    }
  }

  return (
    <>
      {canScrollLeft && (
        <ArrowCircleLeft
          size={theme.size.xl}
          color={theme.palette.primary.main}
          className={classes.arrow}
          style={{ left: '-38px' }}
          onClick={moveLeft}
        />
      )}

      <div id={ID} className={classes.root}>
        {files.map((file, idx) => {
          const key = `${file.name}-${idx}`
          const onDelete = () => removeFile(idx)
          const type = file.type

          if (type.startsWith('image')) {
            return <Image key={key} file={file} onDelete={onDelete} />
          }

          if (type.startsWith('video')) {
            return <Video key={key} file={file} onDelete={onDelete} />
          }

          return <Other key={key} file={file} onDelete={onDelete} />
        })}
      </div>

      {canScrollRight && (
        <ArrowCircleRight
          size={theme.size.xl}
          color={theme.palette.primary.main}
          className={classes.arrow}
          style={{ right: '-38px' }}
          onClick={moveRight}
        />
      )}
    </>
  )
}

export default Attachments
